import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { useState } from "react";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { Input } from "@/components/ui/input";
import { useAllStates } from "@/hooks/use-query-hooks";
import { MapPin } from "lucide-react";

export const Route = createFileRoute("/states/")({
  head: () => buildSeo({
    title: "All US States — Lottery Information",
    description: "Browse lottery games and results for all 50 US states and DC.",
    path: "/states",
  }),
  component: StatesIndex,
});

function StatesIndex() {
  const { data: states } = useAllStates();
  const [search, setSearch] = useState("");
  const filtered = states?.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.abbreviation.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <Layout>
      <SEOHead title="All US States — Lottery Information" description="Browse lottery games and results for all 50 US states and DC." />
      <div className="container py-8">
        <h1 className="font-sans flex items-center gap-3 text-[30px] font-bold leading-tight mb-3 tracking-tight">
          <MapPin className="h-8 w-8 text-primary" strokeWidth={2.25} />
          US State Lottery Directory
        </h1>
        <p className="font-sans text-[16px] leading-6 text-muted-foreground mb-5 max-w-[640px]">
          Browse lottery information for all 50 US states and DC. Find your state's lottery games, current jackpots, and winning numbers. {states && `${states.filter((s) => s.has_lottery).length} states currently operate lotteries.`}
        </p>
        <Input
          placeholder="Search states..."
          aria-label="Search states by name or abbreviation"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="font-sans h-10 max-w-xs mb-8 rounded-lg border bg-card px-3 text-[14px] shadow-sm"
        />
        <h2 className="font-sans text-[20px] font-bold leading-7 mb-4 tracking-tight">States with lottery information</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {filtered?.map((s) => (
            <Link
              key={s.id}
              to="/states/$stateSlug"
              params={{ stateSlug: s.slug }}
              className="font-sans flex min-h-[56px] items-center gap-3 rounded-xl border bg-card px-4 py-3 shadow-sm transition-shadow hover:shadow-md"
            >
              <span className="min-w-[28px] text-[16px] font-bold leading-6 text-primary">{s.abbreviation}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-medium leading-5 text-foreground">{s.name}</p>
                {!s.has_lottery && <span className="mt-0.5 block text-[11px] leading-3 text-muted-foreground">No lottery</span>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}